// angular
import { Component, input } from '@angular/core';
import { CommonModule, NgOptimizedImage } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
// pipes
import { TranslateDataPipe } from '@app/shared/pipes/translate-data.pipe';
import { ImageSourcePipe } from '@app/shared/pipes/image-source.pipe';
// interfaces
import {
  IPeopleProfile,
  PeopleFilterType,
} from '@app/core/shared/interfaces/solutions-profile.interface';
import { PEOPLE_PROFILE_LOGO_PLACEHOLDER } from '@app/shared/constants/people-profile-logo-placeholders.const';
// utils
import {
  profileDisplayBio,
  profileDisplayEmail,
  profileDisplayPhone,
  profileDisplayRegion,
  profileHasBioSection,
  profileHasContactSection,
  profileHasRealPhone,
  profileHasRegionSection,
  profileRealFirstName,
  profileTypeTranslationKey,
  shouldBlurProfileField,
} from '@app/+solutions/shared/utils/solutions-profile-display.util';

@Component({
  selector: 'pwj-supplier-profile-card',
  standalone: true,
  imports: [
    CommonModule,
    NgOptimizedImage,
    TranslateModule,
    TranslateDataPipe,
    ImageSourcePipe,
  ],
  templateUrl: './supplier-profile-card.component.html',
  styleUrl: './supplier-profile-card.component.scss',
})
export class SupplierProfileCardComponent {
  readonly logoPlaceholder = PEOPLE_PROFILE_LOGO_PLACEHOLDER.SUPPLIER;

  profile = input.required<IPeopleProfile>();
  canViewDetails = input(false);
  cardType = input<PeopleFilterType>('SUPPLIER');

  isLocked(): boolean {
    return !this.canViewDetails();
  }

  isRegionLocked(): boolean {
    return shouldBlurProfileField(
      this.canViewDetails(),
      !!this.profile().operating_city_region?.trim()
    );
  }

  isBioLocked(realBio: string): boolean {
    return shouldBlurProfileField(this.canViewDetails(), !!realBio.trim());
  }

  isEmailLocked(): boolean {
    return shouldBlurProfileField(
      this.canViewDetails(),
      !!this.profile().contact_email?.trim()
    );
  }

  isPhoneLocked(): boolean {
    return shouldBlurProfileField(
      this.canViewDetails(),
      profileHasRealPhone(this.profile())
    );
  }

  typeTranslationKey(): string {
    return profileTypeTranslationKey(this.cardType());
  }

  showFirstName(): boolean {
    return !!profileRealFirstName(this.profile());
  }

  firstName(): string {
    return profileRealFirstName(this.profile());
  }

  showBio(realBio: string): boolean {
    return profileHasBioSection(this.profile(), this.canViewDetails(), realBio);
  }

  bio(realBio: string): string {
    return profileDisplayBio(this.profile(), this.canViewDetails(), realBio);
  }

  showRegion(): boolean {
    return profileHasRegionSection(this.profile(), this.canViewDetails());
  }

  region(): string {
    return profileDisplayRegion(this.profile(), this.canViewDetails());
  }

  showContact(): boolean {
    return profileHasContactSection(this.profile(), this.canViewDetails());
  }

  email(): string {
    return profileDisplayEmail(this.profile(), this.canViewDetails());
  }

  phone(): string {
    return profileDisplayPhone(this.profile(), this.canViewDetails());
  }
}
